/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ConferenceFormView.tsx
 🗓️ Created: 2025-10-16
 🧩 Description:
    Conference Room form with 4-column layout for creating and editing conference rooms.
    Includes comprehensive FreeSWITCH conference parameters.

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Copy, Video, Settings, Mic, Disc, Cog, Plus } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useContextStore } from '../../store/context';
import { useAuthStore } from '../../store/auth';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useI18n } from '../../hooks/useI18n';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useConferenceData } from './hooks/useConferenceData';
import { useConferenceForm } from './hooks/useConferenceForm';
import { useConferenceQuickList } from './hooks/useConferenceQuickList';
import { useConferenceDelete } from './hooks/useConferenceDelete';
import { BasicTab, AudioTab, RecordingTab, VideoTab, AdvancedTab } from './tabs';
import { generateRandomPin } from './utils/pinGenerators';
import { createDefaultConferenceData } from './utils/formDefaults';
import type { ConferenceFormViewProps, ConferenceRoom, ConferenceFormTab } from './types';

/**
 * ConferenceFormView component for creating and editing conference rooms
 * Features 4-column grid layout with comprehensive FreeSWITCH parameters
 */
export default function ConferenceFormView({
  onBack,
  onSave,
  conference,
  mode,
  onDuplicate,
}: ConferenceFormViewProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user: currentUser } = useAuthStore();
  const { selectedDomain } = useContextStore();

  // Hook to handle fixed action bar
  useFixedActionBar(true);

  const tenantSlug = currentUser?.tenant?.slug || 'main-tenant';
  const [activeTab, setActiveTab] = useState<ConferenceFormTab>('basic');
  const [originalData, setOriginalData] = useState<ConferenceRoom>(createDefaultConferenceData());

  // Use hooks for data management
  const {
    formData,
    setFormData,
    conferenceProfiles,
    mohClasses,
    isLoadingProfiles,
    isLoadingMoh,
  } = useConferenceData({
    conference,
    mode,
    selectedDomainId: selectedDomain?.id,
    tenantSlug,
    userId: currentUser?.id,
  });

  // Use hook for form management
  const {
    errors,
    isSaving,
    hasChanges,
    handleSave,
    handleCancel: handleFormCancel,
  } = useConferenceForm({
    formData,
    setFormData,
    originalFormData: originalData,
    mode,
    conferenceId: conference?.uuid || conference?.id,
    onSave,
  });

  // Use hook for quick list
  const {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    quickListConferences,
    isSearching,
    isLoadingInitial,
    handleClose: handleQuickListClose,
  } = useConferenceQuickList({
    tenantSlug,
    userId: currentUser?.id,
  });

  // Use hook for delete
  const {
    showDeleteModal,
    setShowDeleteModal,
    isDeleting,
    handleDeleteClick,
    handleDeleteConfirm: handleDeleteConfirmBase,
  } = useConferenceDelete({
    conferenceId: conference?.uuid || conference?.id,
    tenantSlug,
    userId: currentUser?.id,
    onSuccess: onBack,
  });

  // Update original data when conference changes
  useEffect(() => {
    if (conference && mode === 'edit') {
      setOriginalData(conference);
      // Also update formData when conference changes in edit mode
      setFormData(conference);
    }
  }, [conference, mode, setFormData]);

  // Clear page title to show tenant logo + name in header
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => {
      setCurrentPageTitle(null);
    };
  }, [mode, setCurrentPageTitle]);

  // Handle input changes
  const handleInputChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field: keyof ConferenceRoom, value: any) => {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [field]: value,
        };

        // Auto-update Presence ID when name changes (if using default template)
        if (field === 'name') {
          // Only update if it's still using default template or was empty
          if (!prev.presenceId || prev.presenceId.includes('${conference_name}')) {
            updated.presenceId = value ? `${value}@${tenantSlug}` : '';
          }
        }

        return updated;
      });
    },
    [setFormData, tenantSlug]
  );

  // Function to regenerate a PIN
  const regeneratePin = useCallback(
    (field: 'moderatorPin' | 'participantPin') => {
      setFormData((prev) => ({
        ...prev,
        [field]: generateRandomPin(),
      }));
    },
    [setFormData]
  );

  // Handle form submission with action
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const savedData = await handleSave();
      if (savedData) {
        setOriginalData(savedData);
      }
    },
    [handleSave]
  );

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: async () => {
      const savedData = await handleSave();

      // If no changes were detected, return current data to allow navigation
      if (!savedData && mode === 'edit' && conference?.id) {
        // Return current data with ID for navigation purposes
        return { id: conference.id, ...formData };
      }

      if (savedData) {
        setOriginalData(savedData);
      }
      return savedData;
    },
    listPath: '/pbx/applications/conferences',
    newPath: '/pbx/applications/conferences/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/conferences/${savedData.uuid || savedData.id}`,
    getFormData: () => formData,
    storageKey: 'conferences:saveAndNewData',
  });

  // Handle cancel with proper reset
  const handleCancel = useCallback(() => {
    handleFormCancel();
    if (mode === 'edit' && conference) {
      setOriginalData(conference);
    } else {
      setOriginalData(createDefaultConferenceData());
    }
  }, [handleFormCancel, mode, conference]);

  // Handle delete with navigation
  const handleDeleteConfirm = useCallback(async () => {
    await handleDeleteConfirmBase();
  }, [handleDeleteConfirmBase]);

  // Navigate to list (used by "List" button in header)
  const handleGoToList = useCallback(() => {
    onBack();
  }, [onBack]);

  // Navigate to new conference
  const handleAddNew = useCallback(() => {
    navigate('/pbx/applications/conferences/new');
  }, [navigate]);

  // Handle quick list selection
  const handleQuickListSelect = useCallback(
    (selectedConf: ConferenceRoom) => {
      handleQuickListClose();
      navigate(`/pbx/applications/conferences/${selectedConf.uuid || selectedConf.id}`);
    },
    [handleQuickListClose, navigate]
  );

  return (
    <div className="p-6 pb-20">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Video className="h-5 w-5 text-primary" />}
        title={mode === 'create' ? t('conferences:add') : `${t('conferences:edit')} - ${formData.name || formData.extension || ''}`}
        backButtonText={t('common.list')}
        onBack={handleGoToList}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('conferences:actions.quickList'),
            onClick: () => setShowQuickList(true),
          },
          // 2. Duplicate (edit mode only)
          ...(mode === 'edit' && conference && onDuplicate ? [{
            icon: <Copy className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('conferences:actions.duplicate'),
            onClick: () => onDuplicate(conference)
          }] : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('conferences:actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      {/* Tabs */}
      <div className="mt-8">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ConferenceFormTab)}>
          <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 mb-2">
            <TabsTrigger value="basic" icon={<Settings className="w-4 h-4" />}>
              {t('conferences:tabs.basic')}
            </TabsTrigger>
            <TabsTrigger value="audio" icon={<Mic className="w-4 h-4" />}>
              {t('conferences:tabs.audio')}
            </TabsTrigger>
            <TabsTrigger value="recording" icon={<Disc className="w-4 h-4" />}>
              {t('conferences:tabs.recording')}
            </TabsTrigger>
            <TabsTrigger value="video" icon={<Video className="w-4 h-4" />}>
              {t('conferences:tabs.video')}
            </TabsTrigger>
            <TabsTrigger value="advanced" icon={<Cog className="w-4 h-4" />}>
              {t('conferences:tabs.advanced')}
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Basic - General Information */}
          <TabsContent value="basic">
            <BasicTab
              formData={formData}
              errors={errors}
              isEditMode={mode === 'edit'}
              conferenceProfiles={conferenceProfiles}
              loadingProfiles={isLoadingProfiles}
              mohClasses={mohClasses}
              isLoadingMoh={isLoadingMoh}
              onChange={handleInputChange}
              onRegeneratePin={regeneratePin}
            />
          </TabsContent>

          {/* TAB 2: Audio */}
          <TabsContent value="audio">
            <AudioTab
              formData={formData}
              onChange={handleInputChange}
            />
          </TabsContent>

          {/* TAB 3: Recording */}
          <TabsContent value="recording">
            <RecordingTab formData={formData} onChange={handleInputChange} />
          </TabsContent>

          {/* TAB 4: Video */}
          <TabsContent value="video">
            <VideoTab formData={formData} onChange={handleInputChange} />
          </TabsContent>

          {/* TAB 5: Advanced */}
          <TabsContent value="advanced">
            <AdvancedTab formData={formData} onChange={handleInputChange} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Barra de acciones fija */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={handleDeleteClick}
        isEditMode={mode === 'edit'}
        deleteText={t('common.delete')}
        cancelText={t('common.cancel')}
        saveDisabled={isSaving}
        isLoading={isSaving}
        useSaveDropdown={true}
        saveDropdownStorageKey={`conferences:saveAction`}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('conferences:messages.confirmDeleteTitle', 'Delete Conference')}
        message={t('conferences:messages.confirmDeleteMessage', {
          name: formData.name || formData.extension || 'conference',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={handleQuickListClose}
        title={t('conferences:quickList.title')}
        items={quickListConferences.filter((c) => c.id != null) as any}
        isLoadingInitial={isLoadingInitial}
        isSearching={isSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(conf) => handleQuickListSelect(conf as ConferenceRoom)}
        renderItem={(conf) => (
          <button
            onClick={() => handleQuickListSelect(conf as ConferenceRoom)}
            className="w-full text-left p-3 border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold text-primary">{conf.extension}</span>
              <span className="font-medium text-foreground">{conf.name}</span>
            </div>
            {conf.description && (
              <div className="text-sm text-muted-foreground">{conf.description}</div>
            )}
          </button>
        )}
        searchPlaceholder={t('conferences:quickList.searchPlaceholder')}
        emptyMessage={t('conferences:quickList.empty')}
        noResultsMessage={t('conferences:quickList.noResults')}
        headerIcon={<Video className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}
